import React, { useRef, useState } from 'react';
import { Upload } from 'lucide-react';
import { OrbitPrompt } from '../components/OrbitPrompt';
import { TextInput } from '../components/TextInput';
import { SegmentedControl } from '../components/SegmentedControl';
import { WhyWeAsk } from '../components/WhyWeAsk';
import { Actions } from '../components/Actions';

/**
 * The first thing we ask, before the route chooser: give us something to read.
 *
 * Three ways in, because the answer a company has to hand differs — a website,
 * a document (deck, one-pager, org export), or just telling us in their own
 * words. All three land in the same place: the ingest endpoints fill the
 * dossier, so the cards that follow open already filled in rather than blank.
 *
 * This component only collects. The parent owns the ingest calls, so this step
 * reuses whatever read pipeline the surface already has (in Orbit that is
 * sendUrl / sendDoc / sendText) instead of introducing a second one.
 */

export type WebsiteStepMode = 'url' | 'doc' | 'text';

const MODES: { value: WebsiteStepMode; label: string }[] = [
  { value: 'url', label: 'Website' },
  { value: 'doc', label: 'Document' },
  { value: 'text', label: 'Describe it' },
];

const DOC_ACCEPT = '.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.csv,.txt,.md,.rtf,.zip';

export interface WebsiteStepProps {
  /** Called with a URL the user wants us to read. */
  onSubmitUrl: (url: string) => void;
  /** Called with a document the user wants us to read. */
  onSubmitDoc: (file: File) => void;
  /** Called with a free-text description of the business. */
  onSubmitText: (text: string) => void;
  /** Move on without giving us anything. */
  onSkip: () => void;
  onBack?: () => void;
  positionCaption?: string;
  /** True while the parent's read is in flight — locks the controls. */
  isScanning?: boolean;
  /** Why the last attempt did not work. Shown in place of nothing, which is
   *  what a failed read used to say. */
  error?: string | null;
  /** What they typed last time, so a typo is corrected rather than retyped. */
  initialUrl?: string;
}

export function WebsiteStep({
  onSubmitUrl,
  onSubmitDoc,
  onSubmitText,
  onSkip,
  onBack,
  positionCaption,
  isScanning = false,
  error = null,
  initialUrl = '',
}: WebsiteStepProps) {
  const [mode, setMode] = useState<WebsiteStepMode>('url');
  const [url, setUrl] = useState(initialUrl);
  const [text, setText] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const ready =
    (mode === 'url' && url.trim().length > 3)
    || (mode === 'text' && text.trim().length >= 20)
    || (mode === 'doc' && file !== null);

  const submit = () => {
    if (!ready || isScanning) return;
    if (mode === 'url') return onSubmitUrl(url.trim());
    if (mode === 'doc' && file) return onSubmitDoc(file);
    if (mode === 'text') return onSubmitText(text.trim());
  };

  return (
    <>
      <OrbitPrompt eyebrow={`Company · ${positionCaption ?? 'first'}`}>
        Before anything else, <em>what should we read</em> to understand your company?
      </OrbitPrompt>

      <div className="aa-input-area">
        <SegmentedControl
          options={MODES}
          value={mode}
          onChange={setMode}
          ariaLabel="How you want to tell us about the company"
        />

        {mode === 'url' && (
          <TextInput
            value={url}
            onChange={setUrl}
            name="website"
            type="url"
            inputMode="url"
            autoComplete="url"
            spellCheck={false}
            autoFocus
            placeholder="https://yourcompany.com"
            ariaLabel="Website URL"
          />
        )}

        {mode === 'doc' && (
          <div className="aa-website-drop">
            <input
              ref={fileRef}
              type="file"
              accept={DOC_ACCEPT}
              className="aa-visually-hidden"
              onChange={e => setFile(e.target.files?.[0] ?? null)}
            />
            <button
              type="button"
              className="aa-btn aa-btn-ghost"
              onClick={() => fileRef.current?.click()}
            >
              <Upload size={14} strokeWidth={2.5} aria-hidden="true" />
              {file ? 'Choose a different file' : 'Choose a file'}
            </button>
            <span className="aa-website-drop-note">
              {file
                ? file.name
                : 'A deck, a one-pager, a team export, anything that describes the business.'}
            </span>
          </div>
        )}

        {mode === 'text' && (
          <textarea
            className="aa-text-input aa-textarea"
            name="description"
            value={text}
            onChange={e => setText(e.target.value)}
            rows={4}
            autoFocus
            placeholder="What the company does, who it sells to, roughly how many people."
            aria-label="Describe the company"
          />
        )}
      </div>

      {error && (
        <p className="aa-step-error" role="alert">
          {error}
        </p>
      )}

      <WhyWeAsk>
        Whatever you give us here fills in your industry, size, offering and tools,
        so the questions that follow are ones only you can answer. You can correct
        anything we get wrong, and skipping only means typing more later.
      </WhyWeAsk>

      <Actions
        onContinue={submit}
        onBack={onBack}
        onSkip={onSkip}
        hideBack={!onBack}
        continueDisabled={!ready || isScanning}
        continueLabel={isScanning ? 'Reading…' : 'Continue'}
      />
    </>
  );
}

export default WebsiteStep;
